/**
 * Lists saved stations.
 *
 * Two write paths into the registry:
 *   - First successful boot (`useStationStore.connect`) writes a station
 *     row the first time it accepts a BootNotification.
 *   - The inline form on this page (FEAT-004 + NIT-014). The form has
 *     two modes: `add` (blank) and `duplicate-from` (preseeded from an
 *     existing station). Both modes feed `useRegistryStore.add`; the
 *     legacy one-shot `duplicate()` action is no longer invoked from the
 *     UI (kept on the store for back-compat — see its docstring).
 *
 * Two-line label per docs/ADR-001-architecture.md#71:
 *   primary   = displayName if set, else `${cpid}·${displayChecksum}`
 *   secondary = `${cpid}·${displayChecksum}` if displayName set, else wsUrl
 */
import { useEffect, useRef, useState } from 'react';
import type { OcppVersion, StationSnapshot } from '@/state/types';
import { useRegistryStore } from '@/state/useRegistryStore';
import { nextDuplicateCpid } from '@/state/duplicateCpid';
import { computeDisplayChecksum } from '@/lib/computeDisplayChecksum';
import { ToastViewport } from '@/ui/components/Toast';
import { Logo } from '@/ui/components/Logo';
import { useConfirm } from '@/hooks/useConfirm';
import { toast } from '@/state/useToastStore';

type FormMode =
  | { kind: 'add' }
  | { kind: 'duplicate-from'; source: StationSnapshot }
  | { kind: 'edit'; source: StationSnapshot }
  | null;

/**
 * Normalise a raw wsUrl string before saving:
 *  - On HTTPS pages: silently upgrade ws:// → wss://
 *  - Ensure a trailing slash (OCPP endpoint convention)
 */
function normalizeWsUrl(raw: string): { url: string; upgraded: boolean } {
  let url = raw.trim();
  const upgraded =
    typeof window !== 'undefined' &&
    window.location.protocol === 'https:' &&
    url.startsWith('ws://');
  if (upgraded) url = 'wss://' + url.slice(5);
  if (url.length > 0 && !url.endsWith('/')) url += '/';
  return { url, upgraded };
}

/**
 * Returns true when the URL's last path segment matches the cpid —
 * a common mistake where users paste the full WS endpoint including
 * the CP identity into the wsUrl field.
 */
function cpidAppearsInUrl(url: string, cpid: string): boolean {
  if (!cpid || !url) return false;
  const stripped = url.replace(/\/$/, '');
  const lastSegment = stripped.split('/').at(-1) ?? '';
  return lastSegment.toLowerCase() === cpid.trim().toLowerCase();
}

export function RegistryPage(): JSX.Element {
  const entries = useRegistryStore((s) => s.entries);
  const status = useRegistryStore((s) => s.status);
  const refresh = useRegistryStore((s) => s.refresh);
  const remove = useRegistryStore((s) => s.remove);
  const add = useRegistryStore((s) => s.add);
  const update = useRegistryStore((s) => s.update);
  const confirm = useConfirm();

  const [mode, setMode] = useState<FormMode>(null);
  const [cpid, setCpid] = useState('');
  const [wsUrl, setWsUrl] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [ocppVersion, setOcppVersionState] = useState<OcppVersion>('1.6J');
  const [formError, setFormError] = useState<string | null>(null);
  const [upgradedToWss, setUpgradedToWss] = useState(false);
  const formRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const resetForm = (): void => {
    setCpid('');
    setWsUrl('');
    setDisplayName('');
    setOcppVersionState('1.6J');
    setFormError(null);
    setUpgradedToWss(false);
  };

  const openAdd = (): void => {
    resetForm();
    setMode({ kind: 'add' });
  };

  const openDuplicateFrom = (source: StationSnapshot): void => {
    const next = nextDuplicateCpid(
      source.cpid,
      entries.map((s) => s.cpid),
    );
    setCpid(next);
    setWsUrl(source.wsUrl);
    setDisplayName(
      source.displayName !== undefined ? `${source.displayName} (copy)` : '',
    );
    setFormError(null);
    setUpgradedToWss(false);
    setMode({ kind: 'duplicate-from', source });
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const openEdit = (source: StationSnapshot): void => {
    setCpid(source.cpid);
    setWsUrl(source.wsUrl);
    setDisplayName(source.displayName ?? '');
    setOcppVersionState(source.ocppVersion);
    setFormError(null);
    setUpgradedToWss(false);
    setMode({ kind: 'edit', source });
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const closeForm = (): void => {
    setMode(null);
    resetForm();
  };

  const handleSave = async (): Promise<void> => {
    setFormError(null);
    const { url: normalizedUrl, upgraded } = normalizeWsUrl(wsUrl);
    setUpgradedToWss(upgraded);

    if (mode?.kind === 'edit') {
      const result = await update(mode.source.id, {
        cpid,
        wsUrl: normalizedUrl,
        ocppVersion,
        ...(displayName.trim() !== '' ? { displayName: displayName.trim() } : {}),
      });
      if (!result.ok) {
        setFormError(result.error ?? 'Save failed.');
        return;
      }
      toast(`Station ${cpid} updated`);
      closeForm();
      return;
    }

    const result = await add({
      cpid,
      wsUrl: normalizedUrl,
      ...(displayName.trim() !== '' ? { displayName: displayName.trim() } : {}),
      // Duplicate carries the source station's version; Add uses the
      // form's version picker. Edit keeps the existing version (the
      // edit branch above doesn't touch ocppVersion at all).
      ocppVersion: mode?.kind === 'duplicate-from' ? mode.source.ocppVersion : ocppVersion,
    });
    if (!result.ok) {
      setFormError(result.error ?? 'Save failed.');
      return;
    }
    if (mode?.kind === 'duplicate-from') {
      toast(`Duplicated ${mode.source.cpid} → ${cpid}`);
    } else {
      toast(`Station ${cpid} added`);
    }
    closeForm();
  };

  const formHeader =
    mode?.kind === 'duplicate-from'
      ? `Duplicate ${mode.source.cpid}`
      : mode?.kind === 'edit'
        ? `Edit ${mode.source.cpid}`
        : 'Add station';
  const saveLabel =
    mode?.kind === 'duplicate-from' ? 'Save copy' : mode?.kind === 'edit' ? 'Update' : 'Save';

  const showCpidInUrlWarning = cpidAppearsInUrl(wsUrl, cpid);

  return (
    <main className="min-h-full bg-slate-950 p-8 text-slate-200" data-testid="registry-page">
      <header className="mx-auto mb-6 flex max-w-3xl flex-col gap-3 md:flex-row md:items-baseline md:justify-between">
        <div className="flex items-center gap-3">
          <Logo variant="mark" size="lg" testId="registry" href="/welcome" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-100 sm:text-3xl">
              VirtualCharger
            </h1>
            <p className="text-sm text-slate-400">Saved stations</p>
          </div>
        </div>
        <div className="flex flex-wrap items-baseline gap-3">
          <a
            href="?page=vehicles"
            className="text-xs text-slate-400 underline-offset-2 hover:text-slate-100 hover:underline"
            data-testid="registry-vehicles-link"
          >
            {/* VC-B — peer nav link: no decorative arrow (← only marks the true back action). */}
            Vehicles
          </a>
          <span className="text-xs text-slate-500" data-testid="registry-count">
            {entries.length} saved
          </span>
          <button
            type="button"
            onClick={() => {
              if (mode === null) openAdd();
              else closeForm();
            }}
            className="min-h-[44px] rounded bg-primary-hover px-4 py-2 text-sm font-medium text-primary-on hover:bg-primary-base focus:outline-none focus:ring-2 focus:ring-primary-base"
            data-testid="registry-add-toggle"
          >
            {mode === null ? 'Add station' : 'Close'}
          </button>
        </div>
      </header>

      {mode !== null ? (
        <section
          ref={formRef}
          className="mx-auto mb-4 max-w-3xl rounded border border-slate-700 bg-slate-900 p-4"
          data-testid="registry-add-form"
          data-mode={mode.kind}
        >
          <h2
            className="mb-3 text-sm font-medium text-slate-100"
            data-testid="registry-form-header"
          >
            {formHeader}
          </h2>
          <div className="flex flex-wrap gap-2 text-xs">
            <div className="flex flex-col gap-1">
              <input
                type="text"
                value={cpid}
                onChange={(e) => setCpid(e.target.value)}
                placeholder="CP-01"
                className="w-40 rounded border border-slate-600 bg-slate-950 px-2 py-1 font-mono text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
                data-testid="registry-add-cpid"
                aria-label="cpid"
              />
              <span className="text-slate-500">Charge point ID (unique per server)</span>
            </div>
            <div className="flex flex-1 flex-col gap-1">
              <input
                type="text"
                value={wsUrl}
                onChange={(e) => setWsUrl(e.target.value)}
                placeholder="wss://csms.example.com/ocpp/"
                className="w-full rounded border border-slate-600 bg-slate-950 px-2 py-1 font-mono text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
                data-testid="registry-add-wsurl"
                aria-label="wsUrl"
              />
              <span className="text-slate-500">Base URL — do not include the CP ID at the end</span>
            </div>
            <div className="flex flex-col gap-1">
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. Test rack #3"
                className="w-48 rounded border border-slate-600 bg-slate-950 px-2 py-1 text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
                data-testid="registry-add-displayname"
                aria-label="display name"
              />
              <span className="text-slate-500">Display name (optional, not sent to CSMS)</span>
            </div>
            {mode.kind === 'add' || mode.kind === 'edit' ? (
              <div className="flex flex-col gap-1">
                <select
                  value={ocppVersion}
                  onChange={(e) => {
                    const v = e.target.value;
                    setOcppVersionState(v === '2.0.1' ? '2.0.1' : v === '2.1' ? '2.1' : '1.6J');
                  }}
                  className="w-32 rounded border border-slate-600 bg-slate-950 px-2 py-1 text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
                  data-testid="registry-add-ocpp-version"
                  aria-label="OCPP version"
                >
                  <option value="1.6J">OCPP 1.6J</option>
                  <option value="2.0.1">OCPP 2.0.1</option>
                  <option value="2.1">OCPP 2.1</option>
                </select>
                <span className="text-slate-500">OCPP version</span>
              </div>
            ) : null}
            <div className="flex items-start gap-2">
              <button
                type="button"
                onClick={() => { void handleSave(); }}
                className="rounded bg-primary-hover px-3 py-1 text-primary-on hover:bg-primary-base focus:outline-none focus:ring-2 focus:ring-primary-base"
                data-testid="registry-add-save"
              >
                {saveLabel}
              </button>
              <button
                type="button"
                onClick={closeForm}
                className="rounded bg-slate-700 px-3 py-1 text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300"
                data-testid="registry-add-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
          {showCpidInUrlWarning ? (
            <p className="mt-2 text-xs text-amber-400" data-testid="registry-cpid-in-url-warning">
              ⚠ The URL appears to end with your CP ID ({cpid}). The base URL should not include
              the CP ID — it is appended automatically when connecting.
            </p>
          ) : null}
          {upgradedToWss ? (
            <p className="mt-2 text-xs text-sky-400" data-testid="registry-wss-upgrade-notice">
              ↑ ws:// upgraded to wss:// — required when the app runs over HTTPS.
            </p>
          ) : null}
          {formError !== null ? (
            <p className="mt-2 text-xs text-rose-300" data-testid="registry-add-error">
              {formError}
            </p>
          ) : null}
        </section>
      ) : null}

      {status === 'ready' && entries.length === 0 ? (
        <section
          className="mx-auto max-w-3xl rounded border border-slate-700 bg-slate-900 p-6"
          data-testid="registry-empty"
        >
          <p className="text-sm text-slate-300">No saved stations yet.</p>
          <p className="mt-2 text-xs text-slate-500">
            Click <span className="font-medium">Add station</span> above, or open one via URL
            params (e.g.{' '}
            <code className="rounded bg-slate-800 px-1 py-0.5">
              ?CPID=CP-1&amp;WSURL=ws://localhost:8080
            </code>
            ) — it&apos;ll appear here after the first successful boot.
          </p>
        </section>
      ) : null}

      <ul className="mx-auto max-w-3xl space-y-2">
        {entries.map((s) => {
          const checksum = computeDisplayChecksum(s.wsUrl);
          const primary = s.displayName ?? `${s.cpid}·${checksum}`;
          // UX-E — one consistent metadata line on every row: cpid·checksum +
          // the URL (protocol is the badge). Was checksum-or-URL depending on
          // whether a display name was set, which read inconsistently.
          const secondary = `${s.cpid}·${checksum} · ${s.wsUrl}`;
          const openHref = `?station=${encodeURIComponent(s.id)}`;
          return (
            <li
              key={s.id}
              className="flex flex-col gap-3 rounded border border-slate-700 bg-slate-900 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              data-testid={`registry-row-${s.id}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="truncate text-sm font-medium text-slate-100">{primary}</div>
                  {/* UX-A — protocol is a primary differentiator; surface it on the row. */}
                  <span
                    className="shrink-0 rounded bg-slate-700 px-1.5 py-0.5 text-[10px] font-medium text-slate-300"
                    data-testid={`registry-protocol-${s.id}`}
                    title={`OCPP ${s.ocppVersion}`}
                  >
                    {s.ocppVersion}
                  </span>
                </div>
                <div className="truncate text-xs text-slate-500" title={s.wsUrl}>
                  {secondary}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <a
                  href={openHref}
                  className="rounded bg-primary-base px-3 py-1 text-xs font-medium text-primary-on hover:bg-primary-base focus:outline-none focus:ring-2 focus:ring-primary-base"
                  data-testid={`registry-open-${s.id}`}
                >
                  Open
                </a>
                <button
                  type="button"
                  onClick={() => openEdit(s)}
                  className="rounded bg-slate-600 px-3 py-1 text-xs text-slate-100 hover:bg-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-300"
                  data-testid={`registry-edit-${s.id}`}
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => openDuplicateFrom(s)}
                  className="rounded bg-sky-700 px-3 py-1 text-xs text-sky-50 hover:bg-sky-600 focus:outline-none focus:ring-2 focus:ring-sky-300"
                  data-testid={`registry-duplicate-${s.id}`}
                >
                  Duplicate
                </button>
                <button
                  type="button"
                  onClick={() => {
                    confirm(`delete saved station ${primary}`, () => {
                      void remove(s.id).then(() => toast('Station deleted'));
                    });
                  }}
                  className="rounded bg-rose-700 px-3 py-1 text-xs text-rose-50 hover:bg-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-300"
                  data-testid={`registry-delete-${s.id}`}
                >
                  Delete
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      <ToastViewport />
    </main>
  );
}
